import React, { useMemo, useState } from 'react';
import axios from 'axios';

import { useCmsEmbedded } from '~/components/AiChat/hooks/useCmsEmbedded';
import { InvoiceRecommendationAnalysis } from '~/components/AiChat/SideBarDialog/InvoiceRecommendationAnalysis';
import type {
  InvoiceData,
  LineItemProps,
  LineItemRow,
} from '~/components/AiChat/SideBarDialog/InvoiceRecommendationAnalysis/types';
import { NO_COVERAGE } from '~/components/AiChat/SideBarDialog/InvoiceRecommendationAnalysis/utils';
import { SaveInvoiceRequestInvoiceProcessingType } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

import { useClaim } from '../ClaimContainer';
import useFetchExposures from '../exposures/useFetchExposures';
import useCoverageConfigs from '../hooks/useCoverageConfigs';

import { useInvoice } from './hooks/useInvoice';
import { mapInvoiceToInvoiceData, mapLineItemToLineItemProps } from './invoiceUtils';

interface InvoiceAnalysisContainerProps {
  documentId?: number;
  invoiceId?: number;
  open: boolean;
  onClose: () => void;
}

const InvoiceAnalysisContainer: React.FC<InvoiceAnalysisContainerProps> = (props) => {
  // InvoiceAnalysisContainerInner fetches data from the backend,
  // we don't want it to fetch anything if the dialog is not open
  if (!props.open) return null;
  return <InvoiceAnalysisContainerInner {...props} />;
};

const InvoiceAnalysisContainerInner: React.FC<InvoiceAnalysisContainerProps> = ({
  documentId: propDocumentId,
  invoiceId,
  open,
  onClose,
}) => {
  const { invoice, isLoading, isError } = useInvoice({ invoiceId, documentId: propDocumentId });
  const { exposures, isExposuresLoading, isExposuresError } = useFetchExposures();
  const { claim, onClaimUpdate } = useClaim({ claimRequired: true });
  const { coInsuranceRates, applyInvoiceItem } = useCmsEmbedded();
  const { isCoverageConfigsLoading, isCoverageConfigsError, coverageConfigs } = useCoverageConfigs();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Determine documentId to use
  const documentId = propDocumentId || invoice?.document_id;

  const document = useMemo(() => claim?.documents?.find((d) => d.id === documentId), [claim?.documents, documentId]);

  if (!documentId) {
    throw new Error('No documentId available');
  }
  if (!document) {
    throw new Error('Document not found in claim');
  }

  const invoiceData: InvoiceData = useMemo(
    () =>
      invoice && document
        ? mapInvoiceToInvoiceData(document, invoice)
        : {
            date: '',
            number: '',
            vendor: '',
            total_amount: undefined,
            document_claim_internal_id: document?.claim_internal_id,
          },
    [invoice, document]
  );

  const lineItems: LineItemProps[] = useMemo(
    () => invoice?.line_items?.map(mapLineItemToLineItemProps) ?? [],
    [invoice]
  );

  // These are not in dependency arrays in child component, so memoization is optional for performance
  const exposuresByCoverageKey = Object.fromEntries((exposures || []).map((exp) => [exp.coverage_type, exp]));

  const coverages = coverageConfigs.map((c) => ({ value: c.coverage_key, description: c.display_name }));

  const isDisabled = isSubmitting || isError || isExposuresError || isCoverageConfigsError;

  const saveInvoice = async ({
    invoice_data,
    line_items,
  }: {
    invoice_data: InvoiceData;
    line_items: LineItemProps[];
  }): Promise<boolean> => {
    setIsSubmitting(true);
    try {
      const lineItemsToSave = (line_items || []).map((item) => ({
        ...item,
        coverage_key: item.coverage || null,
      }));
      try {
        await axios.post(`/api/v1/claims/${claim.id}/invoice_based_da/save_invoice`, {
          document_id: document.id,
          invoice_id: invoice?.id,
          invoice_number: invoice_data.number || document.document_name || '',
          invoice_date: invoice_data.date,
          vendor: invoice_data.vendor,
          invoice_processing_type: SaveInvoiceRequestInvoiceProcessingType.OdieInvoice,
          line_items: lineItemsToSave,
        });
      } catch (error) {
        await reportAxiosError(error);
        return false;
      }
      await onClaimUpdate();
      return true;
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;
  if (isLoading || isExposuresLoading || isCoverageConfigsLoading) return null;
  if (isError || isExposuresError || isCoverageConfigsError) {
    // reportAxiosError will be called by useInvoice or useFetchExposures
    return null;
  }

  const validateOdieInvoice = (lineItems: LineItemRow[]): string | undefined => {
    const itemsWithMissingDescription = lineItems.some((item) => !item.description || item.description.trim() === '');
    if (itemsWithMissingDescription) {
      return 'Please provide descriptions for all line items';
    }

    const coveredItemsWithoutCoverage = lineItems.some(
      (item) =>
        item.coverage_decision === 'covered' &&
        (!item.coverage || item.coverage === '' || item.coverage === NO_COVERAGE)
    );
    if (coveredItemsWithoutCoverage) {
      return 'Please select coverage for all covered line items';
    }

    return undefined;
  };

  return (
    <InvoiceRecommendationAnalysis
      open={open}
      onClose={onClose}
      invoiceData={invoiceData}
      lineItems={lineItems}
      exposuresByCoverageKey={exposuresByCoverageKey}
      onSave={saveInvoice}
      onApplyCoveredItem={async (item, onUpdate) => {
        setIsSubmitting(true);
        try {
          await applyInvoiceItem({ item, documentId, onUpdate });
        } finally {
          setIsSubmitting(false);
        }
      }}
      coverages={coverages}
      coInsuranceCoverageRates={coInsuranceRates}
      disabled={isDisabled}
      submitting={isSubmitting}
      filePath={`/api/v1/claims/${claim.id}/documents/${documentId}`}
      shouldShowCoverageSummary={true}
      validateBeforeSave={validateOdieInvoice}
      entities={coverages}
    />
  );
};

export default InvoiceAnalysisContainer;
